// src/BookUploadForm.tsx
// NOTE: Update frontend/src/__tests__/BookUploadForm.test.tsx and run
// `../scripts/test-frontend.sh BookUploadForm` when editing helper logic.
import React, { useState, useEffect, useRef } from 'react'
import {
  Button,
  Input,
  Upload,
  Select,
  message,
  Tag,
  Modal,
  Tooltip,
} from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import axios from 'axios'
import type { RcFile } from 'antd/es/upload'
import {
  getAllLanguages,
  isLanguageBeta,
} from './LanguageConfig'
import { isSeriesNumberValid, isFileSizeWithinLimit } from './bookUploadHelpers'
import { tutorialVideoLinks } from './tutorialVideoLinks'
import { auth } from './firebaseAuth'
import './BookUploadForm.css'

const normalizeSeriesInput = (value: string): string =>
  (value || '').replace(/\s+/g, ' ').trim()

interface BookUploadFormProps {
  onUpload: (formData: FormData) => Promise<void>
  onAnalyze: (file: File) => Promise<NewBookPreflightResult>
  onReset: () => void
  authors: string[]
  seriesList: string[]
  simpleMode?: boolean
}

export interface NewBookPreflightResult {
  previewToken: string | null
  detectedSectionCount: number | null
  status: 'ready' | 'needs_formatting' | 'analysis_unavailable'
  reasonCodes: string[]
}

type PreflightState = Omit<NewBookPreflightResult, 'status'> & {
  status: NewBookPreflightResult['status'] | 'analyzing'
}

const CHAPTER_STRUCTURE_REASON_CODE = 'chapter_structure_not_detected'
const CHAPTER_STRUCTURE_HEADING =
  'ScribeShadow couldn’t detect enough separate chapter boundaries.'
const CHAPTER_STRUCTURE_GUIDANCE =
  'Word can look correctly formatted while still missing structural chapter markers. Add a real Word page break before each chapter or apply Word’s built-in Heading 1 style to every chapter title, then select the file again.'

const getErrorMessage = (error: any): string => {
  const detail = error?.response?.data?.message || error?.response?.data?.detail || error?.message
  return typeof detail === 'string' ? detail : ''
}

const isChapterStructureError = (message: string): boolean => {
  const normalized = message.toLowerCase()
  return (
    normalized.includes('could not detect multiple chapters') ||
    normalized.includes('chapters appear too large') ||
    normalized.includes('proper chapter divisions')
  )
}

const BookUploadForm: React.FC<BookUploadFormProps> = ({
  onUpload,
  onAnalyze,
  onReset,
  authors,
  seriesList,
  simpleMode = false,
}) => {
  const [file, setFile] = useState<File | null>(null)
  const [title, setTitle] = useState('')
  const [selectedAuthors, setSelectedAuthors] = useState<string[]>([])
  const [series, setSeries] = useState('')
  const [isBetaUser, setIsBetaUser] = useState(false)
  const [language, setLanguage] = useState('English')
  const [seriesBookNumber, setSeriesBookNumber] = useState('')
  const [seriesNumberError, setSeriesNumberError] = useState('')
  const [fileSizeError, setFileSizeError] = useState<string | null>(null)
  const [authorDropdownOpen, setAuthorDropdownOpen] = useState(false)
  const [seriesDropdownOpen, setSeriesDropdownOpen] = useState(false)
  const [preflight, setPreflight] = useState<PreflightState | null>(null)
  const preflightRunIdRef = useRef(0)

  const LANGUAGES = getAllLanguages(isBetaUser)

  /** Check user’s beta status on mount */
  useEffect(() => {
    const checkBetaStatus = async (): Promise<void> => {
      const userId = auth.currentUser?.uid
      if (!userId) return

      try {
        const { data } = await axios.get<{ isBetaUser: boolean }>(
          `https://askpoeai-nswdzfky7q-uc.a.run.app/check-beta-status/${userId}`,
        )
        setIsBetaUser(data.isBetaUser)
      } catch (err) {
        console.error('Error checking beta status:', err)
      }
    }

    checkBetaStatus()
  }, [auth.currentUser?.uid])

  useEffect(() => {
    const runId = preflightRunIdRef.current + 1
    preflightRunIdRef.current = runId

    if (!file) {
      setPreflight(null)
      return
    }

    setPreflight({
      previewToken: null,
      detectedSectionCount: null,
      status: 'analyzing',
      reasonCodes: [],
    })

    void onAnalyze(file)
      .then((result) => {
        if (preflightRunIdRef.current !== runId) return
        setPreflight(result)
      })
      .catch((error) => {
        if (preflightRunIdRef.current !== runId) return
        const errorMessage = getErrorMessage(error)
        setPreflight({
          previewToken: null,
          detectedSectionCount: null,
          status: 'analysis_unavailable',
          reasonCodes: [
            isChapterStructureError(errorMessage)
              ? CHAPTER_STRUCTURE_REASON_CODE
              : 'document_conversion_failed',
          ],
        })
      })

    return () => {
      if (preflightRunIdRef.current === runId) {
        preflightRunIdRef.current += 1
      }
    }
  }, [file, onAnalyze])

  /** Validate that the series number is numeric (decimals allowed) */
  const validateSeriesNumber = (value: string): boolean => {
    const isValid = isSeriesNumberValid(value)
    if (!isValid) {
      setSeriesNumberError(
        'Series number must be a numeric value (decimals allowed)',
      )
      return false
    }
    setSeriesNumberError('')
    return true
  }

  /** Handle changes to the “Number in Series” field */
  const handleSeriesNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const value = e.target.value
    setSeriesBookNumber(value)
    validateSeriesNumber(value)
  }

  /** Reset all form fields */
  const resetForm = (): void => {
    setFile(null)
    setTitle('')
    setSelectedAuthors([])
    setSeries('')
    setSeriesBookNumber('')
    setLanguage('English')
    setPreflight(null)
  }

  /** Block uploads larger than 30 MB */
  const handleBeforeUpload = (file: RcFile): boolean => {
    const isAllowed = isFileSizeWithinLimit(file.size)
    if (!isAllowed) {
      setFileSizeError('File must be smaller than 30 MB!')
    } else {
      setFileSizeError(null)
    }
    return false // prevent automatic upload, we handle it manually
  }

  /** Submit the form data to the parent */
  const submitForm = async (): Promise<void> => {
    if (!language.trim()) {
      message.error('Please select a language for the book', 5)
      return
    }
    if (!file) {
      message.error('Please select a file to upload', 5)
      return
    }
    if (!validateSeriesNumber(seriesBookNumber)) return

    const formData = new FormData()
    formData.append('file', file)
    formData.append('title', title)
    formData.append('author', selectedAuthors.join(', '))
    formData.append('series', normalizeSeriesInput(series))
    formData.append('language', language.trim())
    if (seriesBookNumber.trim() !== '') {
      formData.append('seriesBookNumber', seriesBookNumber.trim())
    }
    if (preflight?.previewToken) {
      formData.append('preview_token', preflight.previewToken)
    }

    try {
      await onUpload(formData)
      resetForm()
    } catch (err: any) {
      const msg = getErrorMessage(err)
      const displayMessage = isChapterStructureError(msg)
        ? `${CHAPTER_STRUCTURE_HEADING} ${CHAPTER_STRUCTURE_GUIDANCE}`
        : msg.includes('longer than')
          ? 'Error Uploading Book. Please ensure you select the correct language and chapters are properly labeled and/or separated by page breaks.'
          : msg || 'Error uploading book'

      Modal.error({
        title: 'Upload failed',
        content: <div className="book-upload-error-modal-content">{displayMessage}</div>,
        centered: true,
      })
    }
  }

  /** Compact “BETA” tag used throughout the form */
  const BetaTag = (): JSX.Element => (
    <Tag color="orange" className="book-upload-beta-tag">
      BETA
    </Tag>
  )

  const normalizedUploadFilename = file?.name?.toLowerCase() || ''
  const isEpubUpload = normalizedUploadFilename.endsWith('.epub')
  const isDocxUpload = normalizedUploadFilename.endsWith('.docx')
  const exportFormatCopy = isEpubUpload
    ? 'EPUB or Word. EPUB-to-Word conversion may lose some formatting.'
    : isDocxUpload
    ? 'Word.'
    : ''
  const seriesFields = (
    <>
      <div className="book-upload-field">
        <label htmlFor="series">Series Name</label>
        <Select
          id="series"
          data-tour-id="upload-series"
          data-testid="tutorial:upload-book:series"
          mode="tags"
          placeholder="Series Name (optional)"
          value={series ? [series] : []}
          onChange={(value: string | string[]) => {
            const arr = Array.isArray(value) ? value : [value.toString()]
            if (arr.length) {
              setSeries(arr[arr.length - 1])
              setSeriesDropdownOpen(false)
            } else {
              setSeries('')
            }
          }}
          open={seriesDropdownOpen}
          onDropdownVisibleChange={(open) => setSeriesDropdownOpen(open)}
        >
          {seriesList.map((s) => (
            <Select.Option key={s} value={s}>
              {s}
            </Select.Option>
          ))}
        </Select>
      </div>

      <div className="book-upload-field">
        <label htmlFor="bookNbr">Number in Series</label>
        <Input
          id="bookNbr"
          data-tour-id="upload-series-number"
          data-testid="tutorial:upload-book:series-number"
          placeholder="Series Book Number (optional)"
          value={seriesBookNumber}
          onChange={handleSeriesNumberChange}
        />
        {seriesNumberError && (
          <div className="book-upload-field-error">
            <ExclamationCircleOutlined />
            {seriesNumberError}
          </div>
        )}
      </div>
    </>
  )
  const languageField = (
    <div className="book-upload-field book-upload-field-wide">
      <label htmlFor="language">Book’s Current Language</label>
      <Select
        id="language"
        data-tour-id="upload-language"
        data-testid="tutorial:upload-book:language"
        placeholder="Select Language"
        value={language}
        onChange={setLanguage}
      >
        {LANGUAGES.map((lang) => (
          <Select.Option key={lang} value={lang}>
            <span>
              {lang}
              {isLanguageBeta(lang) && <BetaTag />}
            </span>
          </Select.Option>
        ))}
      </Select>
    </div>
  )
  const hasChapterStructureFailure = Boolean(
    preflight?.reasonCodes.includes(CHAPTER_STRUCTURE_REASON_CODE),
  )

  return (
    <div className="book-upload-form" data-testid="tutorial:upload-book:form-ready">
      <section className="book-upload-block book-upload-help-block" data-tour-id="upload-resources">
        <div className="book-upload-block-header">
          <div>
            <div className="book-upload-kicker">Prepare</div>
            <h4>Upload resources</h4>
            <p>Use the tutorial and formatting checklist if this is your first upload.</p>
          </div>
        </div>
        <div
          className="book-upload-link-row"
          data-tour-id="upload-links"
          data-testid="tutorial:upload-book:links"
        >
          <a href={tutorialVideoLinks.uploadFirstBook} target="_blank" rel="noreferrer">
            View Tutorial
          </a>
          <a
            href="/support#word-formatting-guidelines"
            target="_blank"
            rel="noreferrer"
          >
            View Word Formatting Guidelines
          </a>
        </div>
      </section>

      <section className="book-upload-block" data-tour-id="upload-file" data-testid="tutorial:upload-book:file-section">
        <div className="book-upload-block-header">
          <div>
            <div className="book-upload-kicker">File</div>
            <h4>Manuscript file</h4>
            <p>
              {simpleMode ? 'Recommended format: .docx. Also accepted: ' : 'Accepted formats: .docx, '}
              <Tooltip title="EPUB is for advanced users. You should be comfortable unzipping an EPUB and editing its XHTML/CSS/OPF/NCX files. If you’re not, upload a .docx instead.">
                <span className="book-upload-help-trigger">.epub</span>
              </Tooltip>
            </p>
          </div>
          <span
            className={`book-upload-readiness ${file ? 'is-ready' : 'is-needed'}`}
            data-testid={file ? 'tutorial:upload-book:file-selected' : 'tutorial:upload-book:file-needed'}
          >
            {file ? 'File selected' : 'File needed'}
          </span>
        </div>

        <div
          data-tour-id="upload-dropzone"
          data-testid="tutorial:upload-book:dropzone"
          className="book-upload-dropzone"
        >
          <Upload.Dragger
            name="file"
            accept=".docx,.epub"
            multiple={false}
            maxCount={1}
            beforeUpload={handleBeforeUpload}
            onChange={({ fileList }) => {
              const obj = fileList[fileList.length - 1]?.originFileObj
              if (obj && isFileSizeWithinLimit(obj.size)) {
                setFile(obj as File)
              } else {
                setFile(null)
              }
            }}
            onRemove={() => {
              setFile(null)
            }}
          >
            <p className="ant-upload-text">Click or drag book file to upload</p>
          </Upload.Dragger>
        </div>
        {exportFormatCopy && (
          <div className="book-upload-inline-note" data-testid="tutorial:upload-book:export-formats">
            <strong>Export availability:</strong> {exportFormatCopy}
          </div>
        )}
        {isEpubUpload && (
          <div className="book-upload-inline-note">
            <strong>Read this before uploading an EPUB:</strong> EPUBs can occasionally have layout issues after translation
            (headings, line breaks, metadata, TOC). Fixing these requires editing the EPUB
            package (XML/HTML/CSS/OPF). If you’re not familiar with that, use a Word
            (.docx) file and convert it with the tool of your choice after translating.
          </div>
        )}

        {preflight && (
          <div
            className={`book-upload-preflight ${preflight.status === 'ready' ? 'is-green' : 'is-amber'}`}
            role="status"
            aria-live="polite"
            data-testid="book-upload-preflight"
          >
            <strong>
              {preflight.status === 'analyzing'
                ? 'Analyzing chapter structure…'
                : preflight.detectedSectionCount !== null
                ? `ScribeShadow detected ${preflight.detectedSectionCount} chapter ${preflight.detectedSectionCount === 1 ? 'section' : 'sections'}.`
                : hasChapterStructureFailure
                  ? CHAPTER_STRUCTURE_HEADING
                  : 'Chapter analysis is temporarily unavailable.'}
            </strong>
            {preflight.status === 'ready' ? (
              <span>The detected structure looks consistent. You can continue whenever you are ready.</span>
            ) : preflight.status === 'analyzing' ? (
              <span>You can keep completing the form while ScribeShadow checks the manuscript.</span>
            ) : (
              <>
                <span>
                  {preflight.detectedSectionCount !== null
                    ? 'Compare this count with your manuscript. You may still upload it as detected, or correct the chapter formatting first.'
                    : hasChapterStructureFailure
                      ? CHAPTER_STRUCTURE_GUIDANCE
                      : 'You may still upload this manuscript, or review the chapter formatting first.'}
                </span>
                <span className="book-upload-preflight-links">
                  <a href="/support#word-formatting-guidelines" target="_blank" rel="noreferrer">
                    Word formatting guide
                  </a>
                  <a href={tutorialVideoLinks.uploadFirstBook} target="_blank" rel="noreferrer">
                    Watch tutorial
                  </a>
                </span>
              </>
            )}
          </div>
        )}

        {fileSizeError && (
          <div className="book-upload-field-error">
            <ExclamationCircleOutlined />
            {fileSizeError}
          </div>
        )}
      </section>

      <section
        className="book-upload-block"
        data-tour-id="upload-identity"
        data-testid="tutorial:upload-book:identity-section"
      >
        <div className="book-upload-block-header">
          <div>
            <div className="book-upload-kicker">Identity</div>
            <h4>Book details</h4>
            <p>These fields keep your library organized and help group related titles.</p>
          </div>
        </div>
        <div className="book-upload-field-grid">
          <div className="book-upload-field">
            <label htmlFor="title">Title</label>
            <Input
              id="title"
              data-tour-id="upload-title"
              data-testid="tutorial:upload-book:title"
              placeholder="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="book-upload-field">
            <label htmlFor="author">Author(s)</label>
            <Select
              id="author"
              data-tour-id="upload-authors"
              data-testid="tutorial:upload-book:authors"
              mode="tags"
              placeholder="Select or add authors"
              value={selectedAuthors}
              onChange={(value: string | string[]) => {
                const arr = Array.isArray(value) ? value : [value.toString()]
                setSelectedAuthors(arr)
                if (arr.length) {
                  setAuthorDropdownOpen(false)
                }
              }}
              open={authorDropdownOpen}
              onDropdownVisibleChange={(open) => setAuthorDropdownOpen(open)}
            >
              {authors.map((a) => (
                <Select.Option key={a} value={a}>
                  {a}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>
      </section>

      <section
        className="book-upload-block"
        data-tour-id="upload-placement"
        data-testid="tutorial:upload-book:placement-section"
      >
        <div className="book-upload-block-header">
          <div>
            <div className="book-upload-kicker">Series and language</div>
            <h4>Placement</h4>
            <p>Series is optional. The source language is required for translation setup.</p>
          </div>
        </div>
        <div className="book-upload-field-grid">
          {simpleMode ? languageField : (
            <>
              {seriesFields}
              {languageField}
            </>
          )}
        </div>
        {simpleMode && (
          <details className="book-upload-optional-details">
            <summary>Optional details</summary>
            <div className="book-upload-field-grid book-upload-optional-details-grid">
              {seriesFields}
            </div>
          </details>
        )}
      </section>

      <div
        className="book-upload-footer"
        data-tour-id="upload-submit-summary"
        data-testid="tutorial:upload-book:submit-summary"
      >
        <span className="book-upload-footer-summary" aria-live="polite">
          {!file
            ? 'Choose a manuscript file to continue.'
            : !title || !selectedAuthors.length
            ? 'Title and author are required before upload.'
            : 'Ready to upload.'}
        </span>
        <Button
          type="primary"
          onClick={submitForm}
          data-testid="tutorial:upload-book:submit"
          disabled={
            !file ||
            !title ||
            !selectedAuthors.length ||
            !language ||
            !!fileSizeError
          }
        >
          Upload Book
        </Button>
      </div>
    </div>
  )
}

export default BookUploadForm
